/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright (c) 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { createPortal } from 'react-dom';
import { cn } from '@/shared/ui/utils/tailwind';
import { CopyButton } from './widgets';
import { Anchor, Text, TextInput, UnstyledButton } from '@/shared/ui';

/**
 * Renders the modal guiding the user to register the feed URL with a portal.
 *
 * @param props - The setup modal props.
 * @param props.open - Whether the modal is visible.
 * @param props.onClose - Callback invoked to close the modal.
 * @param props.portalName - The display name of the target portal.
 * @param props.feedUrl - The XML feed URL to register.
 * @param props.agencyName - The agency name used in the contact template.
 * @param props.emailContact - The portal contact email address.
 * @param props.t - The translation function.
 * @returns The setup modal element, or null when closed.
 */
export function PortalSetupModal({
  open,
  onClose,
  portalName,
  feedUrl,
  agencyName,
  emailContact,
  t,
}: {
  open: boolean;
  onClose: () => void;
  portalName: string;
  feedUrl: string;
  agencyName: string;
  emailContact: string;
  t: (key: string, options?: any) => string;
}) {
  if (!open) return null;

  const emailSubject = `Cadastro de Feed XML - ${agencyName} - ${portalName}`;
  const emailBody = `Prezada equipe ${portalName},

Solicito o cadastro do nosso feed XML para publicacao de imoveis no portal ${portalName}.

Dados para cadastro:
- Imobiliaria: ${agencyName}
- URL do Feed XML: ${feedUrl}
- Frequencia de atualizacao: Continua (feed sempre atualizado)

Favor confirmar o cadastro e informar quando o crawler iniciara a leitura.

Atenciosamente,
${agencyName}`;

  const mailtoLink = `mailto:${emailContact}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-[var(--surface-inverse)]/40" />
      <div
        className="relative w-full max-w-lg mx-(--space-4) bg-(--surface-primary) border border-(--border-subtle) shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-(--space-6) py-(--space-4) border-b border-(--border-subtle)">
          <Text className="m-0 text-base font-semibold text-(--text-primary)">
            {t('portal.modal.conectar', { portalName })}
          </Text>
          <UnstyledButton
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-(--radius-md) border-none bg-transparent hover:bg-(--surface-secondary) cursor-pointer text-(--text-tertiary) text-lg"
          >
            &times;
          </UnstyledButton>
        </div>

        <div className="px-(--space-6) py-(--space-5) space-y-(--space-4) max-h-[70vh] overflow-y-auto">
          <Text className="m-0 text-xs text-(--text-tertiary)">
            {t('portal.modal.descricao', { portalName })}
          </Text>

          <div className="space-y-(--space-3)">
            <div className="flex gap-(--space-3)">
              <Text component="span" className="flex-none w-6 h-6 rounded-full bg-(--seed-base) text-[var(--text-inverse)] text-xs font-bold flex items-center justify-center">1</Text>
              <div className="flex-1">
                <Text className="m-0 text-sm font-medium text-(--text-primary)">{t('portal.modal.passo1Titulo')}</Text>
                <div className="mt-(--space-1-5) flex items-center gap-(--space-2)">
                  <TextInput
                    readOnly
                    value={feedUrl}
                    className={cn(
                      'flex-1 px-(--space-2-5) py-(--space-2) text-xs rounded border border-(--border-subtle)',
                      'bg-(--surface-secondary) text-(--text-primary)',
                    )}
                  />
                  <CopyButton text={feedUrl} label={t('portal.copyButton.copiar')} copiedLabel={t('portal.copyButton.copiado')} className="py-(--space-2)" />
                </div>
              </div>
            </div>

            <div className="flex gap-(--space-3)">
              <Text component="span" className="flex-none w-6 h-6 rounded-full bg-(--seed-base) text-[var(--text-inverse)] text-xs font-bold flex items-center justify-center">2</Text>
              <div className="flex-1">
                <Text className="m-0 text-sm font-medium text-(--text-primary)">{t('portal.modal.passo2Titulo')}</Text>
                <Text className="m-0 mt-(--space-0-5) text-xs text-(--text-tertiary)">
                  {t('portal.modal.passo2Descricao')}
                </Text>
                <Text className="m-0 mt-(--space-1) text-xs text-(--text-primary)">{emailContact}</Text>
              </div>
            </div>

            <div className="flex gap-(--space-3)">
              <Text component="span" className="flex-none w-6 h-6 rounded-full bg-(--seed-base) text-[var(--text-inverse)] text-xs font-bold flex items-center justify-center">3</Text>
              <div className="flex-1">
                <Text className="m-0 text-sm font-medium text-(--text-primary)">{t('portal.modal.passo3Titulo')}</Text>
                <Text className="m-0 mt-(--space-0-5) text-xs text-(--text-tertiary)">
                  {t('portal.modal.passo3Descricao')}
                </Text>
              </div>
            </div>
          </div>

          <div className="pt-(--space-3) border-t border-(--border-subtle) space-y-(--space-2)">
            <Text className="m-0 text-xs font-semibold text-(--text-primary)">{t('portal.modal.modeloEmail')}</Text>
            <div className="p-(--space-3) rounded-(--radius-md) bg-(--surface-secondary) border border-(--border-subtle)">
              <Text className="m-0 text-xs font-semibold text-(--text-secondary) mb-(--space-1)">{t('portal.modal.assunto', { subject: emailSubject })}</Text>
              <pre className="m-0 text-xs text-(--text-primary) whitespace-pre-wrap font-sans leading-relaxed">{emailBody}</pre>
            </div>
            <div className="flex gap-(--space-2)">
              <CopyButton text={emailBody} label={t('portal.modal.copiarTexto')} copiedLabel={t('portal.copyButton.copiado')} />
              <Anchor
                href={mailtoLink}
                className={cn(
                  'px-(--space-3) py-(--space-1-5) text-xs rounded font-medium no-underline',
                  'border border-(--seed-base) cursor-pointer hover:opacity-90',
                )}
                style={{ color: 'var(--seed-base)' }}
              >
                {t('portal.modal.abrirNoEmail')}
              </Anchor>
            </div>
          </div>
        </div>

        <div className="px-(--space-6) py-(--space-3) border-t border-(--border-subtle) flex justify-end">
          <UnstyledButton
            onClick={onClose}
            className="px-(--space-4) py-(--space-2) text-xs rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-secondary) hover:bg-(--surface-primary) cursor-pointer"
          >
            {t('portal.modal.fechar')}
          </UnstyledButton>
        </div>
      </div>
    </div>,
    document.body
  );
}
